import TabContext from '@mui/lab/TabContext';
import TabPanel from '@mui/lab/TabPanel';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Redirect,
  useHistory,
  useLocation,
  useParams,
} from 'react-router';
import { setAccountFarm, setAccountFarmsOptions } from '../../../../actions/accountFarmAction';
import { setCurrentSector } from '../../../../actions/currentSectorActions';
import { setAllFarmDevices, setDashboardType } from '../../../../actions/farmSectorActions';
import { setUserAccount } from '../../../../actions/userAccountAction';
import BiweeklyReportTab from '../../../../components/BiweeklyReport/BiweeklyReportTab';
import CustomGrapherTab from '../../../../components/CustomGrapher/CustomGrapherTab';
import CustomButton from '../../../../components/General/CustomButton';
import CustomConfirmationModal from '../../../../components/General/CustomConfirmationModal';
import CustomHeader from '../../../../components/General/CustomHeader';
import AlertsTab from '../../../../components/Highcharts/AlertsTab/AlertsTab';
import { ALL_ALERTS_LIST_UI } from '../../../../components/Highcharts/AlertsTab/constants';
import MapWrapper from '../../../../components/Layout/Google/MapWrapper';
import Page from '../../../../components/Layout/Page';
import { UserRole, UserType } from '../../../../constants/user_types';
import useChartFilter from '../../../../hooks/useChartFilter';
import useFetchApi from '../../../../hooks/useFetchApi';
import useNodes from '../../../../hooks/useNodes';
import useSensorModels from '../../../../hooks/useSensorModels';
import apiClient from '../../../../requests/api/apiClient';
import {
  CultivationCyclePopulatedSchema,
  FarmSchema,
  PhenologicalStateSectorResponse,
  PhenologicalStateWithCycle,
  SectorDetailSchema,
  SectorSchema,
  SensorModelSchema,
  VarietySchema,
} from '../../../../requests/api/apiTypes';
import { ReduxState } from '../../../../types';
import { FetchError } from '../../../types';
import RiegoSectorDetail from '../../Riego/SectorDetail';
import {
  RouteParams,
} from '../types';
import CultivationCyclesList from './component/CultivationCyclesList';
import Dashboards from './component/Dashboards';
import ChartRulerButton from './component/ChartRulerButton';
import ChartExceptionButton from './component/ChartExceptionButton';
import DashboardDownloadButton from './component/DashboardDownloadButton';
import { DataRecoveryCard } from './component/DataRecoveryCard';
import FilterSidebar from './component/FilterSidebar';
import PhenologicalStatesList from './component/PhenologicalStatesList';
import SectorPolygonMap from './component/SectorPolygonMap';
import SectorTabsSidebar from './component/SectorTabsSidebar';
import useStyles from './styles';
import { SOIL_TYPES } from './types';
import { ChartRulerProvider } from '../../../../context/ChartRulerContext';
import { ChartExceptionProvider } from '../../../../context/ChartExceptionContext';
import { DashboardDownloadProvider } from '../../../../context/DashboardDownloadContext';
import {
  getInheritedConfig,
  getIrrigationValueFromSectorInfo,
  getParentSectorHelperText,
  getSectorChildren,
  handleCancelTabChange,
  handleConfirmTabChange,
  handleDeleteSector,
  handleUpdateIrrigationConfig,
  handleUpdateSectorInformation,
  InheritedConfig,
  IrrigationConfig,
} from './utils';

const DASHBOARD_TYPE_TO_PARAM: Record<number, string> = {
  1: 'suelo',
  2: 'planta',
  3: 'suelo-salinidad-temperatura',
  4: 'clima',
  5: 'suelo-todos',
  6: 'planta-todos',
  7: 'riego-todos',
  8: 'clima-todos',
};

const SECTOR_TAB_STORAGE_KEY = 'sector-tab';
const NO_PARENT_SECTOR = '1';
const DEFAULT_TELEMETRY_TYPE = 'wiseconn';

const toDateInput = (date: string | number | Date): string => new Date(date).toISOString().split('T')[0];

const getDashboardDownloadFilename = (dashboardType: number): string => {
  const param = DASHBOARD_TYPE_TO_PARAM[dashboardType];
  return param ? `dashboard_${param.replace(/-/g, '_')}` : 'dashboard';
};

const SectorDetail = () => {
  const classes = useStyles();

  const { id } = useParams<RouteParams>();

  const sectorId = id?.split('?')[0];

  const [value, setValue] = useState(() => {
    const savedTab = localStorage.getItem(SECTOR_TAB_STORAGE_KEY);
    return savedTab || '1';
  });
  const [notFound, setNotFound] = useState(false);
  const [sectorInfo, setSectorInfo] = useState<SectorDetailSchema | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  const [varietiesList, setVarietiesList] = useState<VarietySchema[] | undefined>(undefined);
  const [speciesList, setSpeciesList] = useState<{ id?: string; name?: string }[]>([]);
  // Estas 2 deberian irse al tab correspondiente
  const [selectedSpecies, setSelectedSpecies] = useState<string>('');
  const [selectedVarietyId, setSelectedVarietyId] = useState<string | undefined>('');

  const [cultivationCycles, setCultivationCycles] = useState<CultivationCyclePopulatedSchema[]>([]);
  const [brotherSectors, setBrotherSectors] = useState<SectorDetailSchema[]>([]);

  const history = useHistory();
  const location = useLocation();

  const { enqueueSnackbar } = useSnackbar();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const { selectedFarm } = useSelector((state: ReduxState) => state.accountFarm);
  const selectedSector = useSelector((state: ReduxState) => state.currentSector.selectedSector);
  const { user } = useSelector((state: ReduxState) => state.account);
  const { selectedAccount } = useSelector((state: ReduxState) => state.userAccount);
  const { localAccounts } = useSelector((state: ReduxState) => state.localAccounts);
  const dispatch = useDispatch();
  const dashboardType = useSelector((state: ReduxState) => state.farmSector.dashboardType);

  const [weatherStations, setWeatherStations] = useState<any[]>([]);

  const [loadingSaveSector, setLoadingSaveSector] = useState(false);

  const [
    phenologicalStatesWCycle, setPhenologicalStatesWCycle,
  ] = useState<PhenologicalStateWithCycle>({});

  const [refreshPhenologicalStates, setRefreshPhenologicalStates] = useState(false);

  const [inheritedConfig, setInheritedConfig] = useState<InheritedConfig>({ irrigationConfig: {} });

  const [pendingTabChange, setPendingTabChange] = useState<string | null>(null);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [originalSectorInfo,
    setOriginalSectorInfo] = useState<SectorDetailSchema | undefined>(undefined);

  const [isEditingInfoTab, setIsEditingInfoTab] = useState(false);

  const isAdminCDTEC = user?.role === UserRole.ADMIN && user?.type === UserType.CDTEC;
  const isCDTEC = user?.type === UserType.CDTEC;
  const isConsultantAgronomist = user?.role === UserRole.AGRO && user?.type === UserType.CONSULTANT;
  const isAdmin = user?.role === UserRole.ADMIN;

  // Permissions logic
  const canEdit = isAdminCDTEC || isCDTEC || isConsultantAgronomist || isAdmin;
  const canDelete = isAdminCDTEC || isCDTEC;

  const [collapsed, setCollapsed] = useState(false);
  const [filterCollapsed, setFilterCollapsed] = useState(false);

  const { nodes } = useNodes(selectedFarm?.id ?? '');
  const { sensorModels } = useSensorModels(selectedFarm?.id ?? '');

  const {
    tempStartDate,
    tempEndDate,
    selectedInterval,
    setTempStartDate,
    setTempEndDate,
    handleApplyDates,
    handleIntervalSelect,
  } = useChartFilter();

  const [selectedCultivationCycle, setSelectedCultivationCycle] = useState<string>('');
  const [selectedPhenologicalState, setSelectedPhenologicalState] = useState<string>('');

  const handleCultivationCycleChange = (cycleId: string) => {
    setSelectedCultivationCycle(cycleId);
    setSelectedPhenologicalState(''); // Reset phenological state when cycle changes

    if (cycleId) {
      const selectedCycle = cultivationCycles.find((cycle) => cycle.id === cycleId);
      if (selectedCycle) {
        setTempStartDate(toDateInput(selectedCycle.startDate));
        setTempEndDate(toDateInput(selectedCycle.endDate));
      }
    }
  };

  const handlePhenologicalStateChange = (stateId: string) => {
    setSelectedPhenologicalState(stateId);

    if (stateId && selectedCultivationCycle) {
      const states = phenologicalStatesWCycle[selectedCultivationCycle];
      const selectedState = states?.find((state) => state.id === stateId);
      if (selectedState) {
        setTempStartDate(toDateInput(selectedState.startDate));
        setTempEndDate(toDateInput(selectedState.endDate));
      }
    }
  };

  const getVisibleTabs = () => {
    if (!user) return [];

    const userType = user.type;
    const userRole = user.role;
    const clientTabs = ['1', '2', '3', '4', '7', '8', '9', '10'];

    if (userType === UserType.CLIENT && userRole === UserRole.OPERATOR) {
      return ['1', '2', '4', '7', '8', '9', '10'];
    }

    // CDTEC users have access to all tabs
    if (userType === UserType.CDTEC) {
      return ['1', '2', '3', '4', '5', '7', '8', '9', '10'];
    }

    if (userType === UserType.CONSULTANT) {
      return ['1', '2', '3', '4', '7', '8', '9', '10'];
    }

    return clientTabs;
  };

  const { data: phenologicalStatesData } = useFetchApi<PhenologicalStateSectorResponse>({
    modelEndpoint: 'phenologicalStateSectorList',
    modelName: 'phenologicalState',
    id: sectorId,
    dependencies: [sectorId, refreshPhenologicalStates],
  });

  useEffect(() => {
    if (phenologicalStatesData?.phenologicalStates) {
      setPhenologicalStatesWCycle(phenologicalStatesData.phenologicalStates);
    }
  }, [phenologicalStatesData?.phenologicalStates]);

  useEffect(() => {
    if (nodes && sensorModels) {
      dispatch(setAllFarmDevices([...nodes, ...sensorModels]));
    }
  }, [nodes, sensorModels]);

  useEffect(() => {
    const getWeatherStations = async () => {
      if (selectedFarm?.id) {
        const response = await apiClient.sensorModels.sensorModelsByFarmId(selectedFarm?.id);
        setWeatherStations(response?.data?.sensorModels?.filter(
          (sensorModel: SensorModelSchema) => sensorModel.isWeatherStation === true,
        ) ?? []);
      }
    };
    getWeatherStations();
  }, []);

  const syncAccountAndFarmFromSector = () => {
    if (!sectorInfo?.farm || !localAccounts?.length) return;
    const farmId = sectorInfo.farm as string;
    const accountWithFarm = localAccounts.find(
      (account) => account.farms?.some((f) => f.id === farmId),
    );
    if (!accountWithFarm) return;
    const foundFarm = accountWithFarm.farms?.find((f) => f.id === farmId);
    if (!foundFarm) return;
    if (selectedAccount?.id !== accountWithFarm.id) {
      dispatch(setUserAccount(accountWithFarm));
      dispatch(setAccountFarmsOptions(accountWithFarm.farms || []));
    }
    if (selectedFarm?.id !== foundFarm.id) {
      dispatch(setAccountFarm(foundFarm as FarmSchema));
    }
  };

  useEffect(() => {
    syncAccountAndFarmFromSector();
  }, [sectorInfo?.farm, localAccounts, selectedAccount?.id, selectedFarm?.id]);

  useEffect(() => {
    const getSectorInformation = async () => {
      setLoading(true);
      try {
        const { data } = await apiClient.sector.sectorDetail(sectorId);
        const sectorData = data.sector;
        dispatch(setCurrentSector(sectorData as SectorSchema));
        setSectorInfo(sectorData);
        setOriginalSectorInfo(JSON.parse(JSON.stringify(sectorData)));
        const searchParams = new URLSearchParams(location.search);
        if (!searchParams.get('farmId') && sectorData?.farm) {
          searchParams.set('farmId', sectorData.farm as string);
          history.replace(
            `${location.pathname}?${searchParams.toString()}`,
          );
        }
        // Get brother sectors using farmId
        const { data: farmSectorsData } = await apiClient.sectors.sectorsDetail(sectorData?.farm ?? '');
        const farmSectors = farmSectorsData.sectors;
        // Set brother sectors to all except the current sector
        setBrotherSectors(farmSectors?.filter(
          (sector: SectorSchema) => sector.id !== sectorId,
        ) ?? []);
      } catch (err) {
        const { error } = err as FetchError;
        enqueueSnackbar(error.message, { variant: 'error' });
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    const getVarietiesList = async () => {
      try {
        const varietiesResponse = await apiClient.varieties.varietiesList();
        const varietiesInformation = varietiesResponse.data?.varieties?.map((variety) => ({
          id: variety.id,
          name: variety.name,
          species: variety.species,
          phenologicalStates: variety.phenologicalStates,
        })) || [];

        setVarietiesList(varietiesInformation);
      } catch (err) {
        const { error } = err as FetchError;
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    };

    const getCultivationCycles = async () => {
      try {
        const { data } = await apiClient.cultivationCycleSector.cultivationCycleSectorDetail(
          sectorId,
        );
        const cultivationCyclesData = data.cultivationCycles;
        setCultivationCycles(cultivationCyclesData);
      } catch (err) {
        const fetchErr = err as FetchError;
        const message = fetchErr?.error?.message
          ?? (err as Error)?.message
          ?? 'Error al obtener los ciclos de cultivo';
        enqueueSnackbar(message, { variant: 'error' });
      }
    };

    const getSpeciesList = async () => {
      try {
        const { data } = await apiClient.species.speciesList();
        setSpeciesList(data?.species || []);
      } catch (err) {
        const { error } = err as FetchError;
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    };

    getSectorInformation();
    getVarietiesList();
    getCultivationCycles();
    getSpeciesList();
  }, [sectorId]);

  useEffect(() => {
    if (sectorInfo && varietiesList) {
      const selectedVariety = varietiesList.find((variety) => variety.id === sectorInfo.variety);
      if (selectedVariety) {
        setSelectedSpecies(selectedVariety?.species || '');
        setSelectedVarietyId(selectedVariety.id);
      }
    }
  }, [sectorInfo, varietiesList]);

  const filteredVarieties = varietiesList?.filter(
    (variety) => variety.species === selectedSpecies,
  ) || [];

  const selectedVarietyName = useMemo(() => (varietiesList || [])?.find(
    (variety) => variety.id === selectedVarietyId,
  )?.name, [varietiesList, selectedVarietyId]);

  const validateNumericInput = (inputValue: string): boolean => {
    if (inputValue === '') return true;
    const numericRegex = /^-?\d*\.?\d*$/;
    return numericRegex.test(inputValue);
  };

  const handleFieldChange = (field: keyof SectorDetailSchema, newValue: string | number) => {
    if (field === 'theoreticalSurface' && typeof newValue === 'string') {
      if (!validateNumericInput(newValue)) {
        return;
      }
    }

    setSectorInfo((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        [field]: newValue,
      };
    });
    setIsEditingInfoTab(true);
  };

  const handleVarietyChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedId = event.target.value;
    setSelectedVarietyId(selectedId);
    handleFieldChange('variety', selectedId);
  };

  // Handle tab value changes when user permissions change
  useEffect(() => {
    const visibleTabs = getVisibleTabs();
    if (!visibleTabs.includes(value)) {
      const defaultTab = visibleTabs.length > 0 ? visibleTabs[0] : '1';
      setValue(defaultTab);
      localStorage.setItem(SECTOR_TAB_STORAGE_KEY, defaultTab);
    }
  }, []);

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    if (value === '2' && isEditingInfoTab) {
      setPendingTabChange(newValue);
      setShowUnsavedChangesModal(true);
    } else {
      setValue(newValue);
      localStorage.setItem(SECTOR_TAB_STORAGE_KEY, newValue);
    }
  };

  const handleSaveChanges = async () => {
    await handleUpdateSectorInformation(
      sectorInfo,
      originalSectorInfo,
      sectorId,
      selectedFarm,
      brotherSectors,
      enqueueSnackbar,
      setLoadingSaveSector,
      dispatch,
      setSectorInfo,
      setInheritedConfig,
    );
    if (originalSectorInfo) {
      setOriginalSectorInfo(JSON.parse(JSON.stringify(sectorInfo)));
    }
    setIsEditingInfoTab(false);
    handleConfirmTabChange(
      pendingTabChange,
      setValue,
      setPendingTabChange,
      setShowUnsavedChangesModal,
    );
  };

  const isParentSectorAssigned = Boolean(
    sectorInfo?.parentSector && sectorInfo.parentSector !== NO_PARENT_SECTOR,
  );

  const sectorChildren = getSectorChildren(sectorInfo, brotherSectors);
  const hasSectorChildren = sectorChildren.length > 0;

  const handleIrrigationFieldChange = (
    field: keyof IrrigationConfig,
    newValue: string | number,
  ) => {
    const valueStr = String(newValue);
    if (!validateNumericInput(valueStr) && field !== 'typeOfIrrigation') {
      return;
    }

    setSectorInfo((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        irrigationConfig: {
          ...(prev.irrigationConfig as IrrigationConfig),
          [field]: newValue,
        },
      };
    });
  };

  useEffect(() => {
    if (sectorInfo?.parentSector && sectorInfo.parentSector !== NO_PARENT_SECTOR) {
      getInheritedConfig(sectorInfo.parentSector, setInheritedConfig, setSectorInfo, true);
    } else if (sectorInfo?.parentSector === NO_PARENT_SECTOR) {
      setInheritedConfig({ irrigationConfig: {} });
      setSectorInfo((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          irrigationConfig: {},
        };
      });
    }
  }, [sectorInfo?.parentSector]);

  const farmMatchesSector = selectedFarm?.id
    && sectorInfo?.farm
    && selectedFarm.id === sectorInfo.farm;
  const breadcrumb = farmMatchesSector
    ? `${selectedFarm?.name}/${sectorInfo?.name ?? ''}`
    : `${sectorInfo?.name ?? ''}`;

  if (notFound) {
    return <Redirect to="/layout" />;
  }

  if (!sectorInfo) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  let filterSidebarPaddingRight = '0px';
  if (value === '1') {
    filterSidebarPaddingRight = filterCollapsed ? '0px' : '310px';
  }

  return (
    <Page
      className={classes.root}
      title="Configuración Campo"
    >
      <Container maxWidth={false} >
        <ChartExceptionProvider>
        <ChartRulerProvider>
          <DashboardDownloadProvider>
            <Box
              height='100%'
              display='flex'
              pt={0.5}
              sx={{
                paddingRight: filterSidebarPaddingRight,
                transition: 'padding-right 0.2s ease',
              }}
            >
              <SectorTabsSidebar
                tabs={getVisibleTabs().map((tab) => {
                  switch (tab) {
                    case '1': return { value: '1', label: 'Visualización' };
                    case '2': return { value: '2', label: 'Información' };
                    case '3': return { value: '3', label: 'Estados fenológicos' };
                    case '4': return { value: '4', label: 'Alertas' };
                    case '5': return { value: '5', label: 'Variables derivadas' };
                    case '7': return { value: '7', label: 'Configuración Riego' };
                    case '8': return { value: '8', label: 'Riego' };
                    case '9': return { value: '9', label: 'Informe quincenal' };
                    case '10': return { value: '10', label: 'Graficador' };
                    default: return { value: tab, label: tab };
                  }
                })}
                value={value}
                onChange={(newValue) => handleTabChange(
                  { preventDefault: () => { } } as React.SyntheticEvent,
                  newValue,
                )}
                collapsed={collapsed}
                setCollapsed={setCollapsed}
              />
              <Box flex={1} minWidth={0} display="flex" flexDirection="column">
                <CustomHeader
                  title={breadcrumb}
                  showBackButton={true}
                  onBack={() => {
                    history.push('/inicio');
                  }}
                  sectors={true}
                  ActionButton={value === '1' ? (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      <ChartRulerButton />
                      <ChartExceptionButton sx={{ marginBottom: '8px' }} />
                      <DashboardDownloadButton
                        downloadFilename={getDashboardDownloadFilename(dashboardType)}
                        sx={{
                          marginRight: '16px',
                        }}
                      />
                    </Box>
                  ) : undefined}
                />
                <TabContext value={value}>
                  <TabPanel value="1">
                    {loading ? (
                      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                      </Box>
                    ) : (
                      <Dashboards
                        sectorId={sectorId}
                      />
                    )}
                  </TabPanel>
                  <TabPanel value="2">
                    <Box mt={1}>
                      <Card>
                        <CardContent>
                          <Typography variant="h4" color="primary" gutterBottom>
                            General
                          </Typography>
                          <Divider className={classes.divider} />
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Identificación cliente"
                                value={sectorInfo?.name || ''}
                                onChange={(e) => handleFieldChange('name', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={!canEdit} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Superficie teórica [HA]"
                                type="text"
                                value={sectorInfo?.theoreticalSurface || ''}
                                onChange={(e) => handleFieldChange('theoreticalSurface', e.target.value)}
                                fullWidth variant="standard"
                                disabled={!canEdit}
                                placeholder="0.0" />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Wiseconn ID"
                                value={sectorInfo?.wiseconnId || ''}
                                onChange={(e) => handleFieldChange('wiseconnId', e.target.value)}
                                fullWidth variant="standard"
                                disabled={!isCDTEC} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Equipo de riego"
                                value={sectorInfo?.sectorDevice || ''}
                                onChange={(e) => handleFieldChange('sectorDevice', e.target.value)}
                                fullWidth variant="standard"
                                disabled={!canEdit} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Tipo de suelo"
                                value={sectorInfo?.typeOfSoil || ''}
                                onChange={(e) => handleFieldChange('typeOfSoil', e.target.value)}
                                fullWidth
                                select
                                variant="standard"
                                disabled={!canEdit}
                              >
                                {SOIL_TYPES.map((soilType) => (
                                  <MenuItem key={soilType} value={soilType}>
                                    {soilType}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Especie"
                                value={selectedSpecies}
                                onChange={(e) => {
                                  setSelectedSpecies(e.target.value);
                                  setSelectedVarietyId('');
                                }}
                                fullWidth
                                select
                                variant="standard"
                                disabled={!canEdit}
                              >
                                {speciesList
                                  .sort((a, b) => (a.name || '')
                                    .localeCompare(b.name || ''))
                                  .map((species) => (
                                    <MenuItem key={species.id} value={species.name}>
                                      {species.name}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Variedad"
                                value={selectedVarietyId}
                                onChange={handleVarietyChange}
                                fullWidth
                                select
                                variant="standard"
                                disabled={!canEdit}
                              >
                                {filteredVarieties.map((variety) => (
                                  <MenuItem key={variety.id} value={variety.id}>
                                    {variety.name}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Año de plantación"
                                type="number"
                                value={sectorInfo?.plantingYear || ''}
                                onChange={(e) => handleFieldChange('plantingYear', parseInt(e.target.value, 10))}
                                fullWidth
                                variant="standard"
                                disabled={!canEdit} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Marco de plantación"
                                value={sectorInfo?.plantingFrame || ''}
                                onChange={(e) => handleFieldChange('plantingFrame', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={!canEdit} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Encargado"
                                value={sectorInfo?.managerName || ''}
                                onChange={(e) => handleFieldChange('managerName', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={!canEdit} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Número de sector"
                                value={sectorInfo?.sectorNumber || ''}
                                onChange={(e) => handleFieldChange('sectorNumber', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={!canEdit} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Estacion de clima"
                                value={sectorInfo?.weatherZoneId || ''}
                                onChange={(e) => handleFieldChange('weatherZoneId', e.target.value)}
                                fullWidth
                                select
                                variant="standard"
                                disabled={!isCDTEC}
                              >
                                <MenuItem value="">Ninguno</MenuItem>
                                {weatherStations.map((sensor: any) => (
                                  <MenuItem key={sensor.id} value={sensor.zoneId}>
                                    {`Nombre: ${sensor.name ?? 'N/A'} - ${sensor.zoneId}`}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Helius"
                                value={sectorInfo?.sn || ''}
                                onChange={(e) => handleFieldChange('sn', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={!isCDTEC} />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Tipo de Telemetría"
                                value={sectorInfo?.telemetryType || DEFAULT_TELEMETRY_TYPE}
                                onChange={(e) => handleFieldChange('telemetryType', e.target.value || DEFAULT_TELEMETRY_TYPE)}
                                fullWidth
                                select
                                variant="standard"
                                disabled={!isCDTEC}
                              >
                                <MenuItem value="wiseconn">Wiseconn</MenuItem>
                                <MenuItem value="sn">Helius</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Sector padre"
                                value={sectorInfo?.parentSector || NO_PARENT_SECTOR}
                                onChange={(e) => handleFieldChange('parentSector', e.target.value)}
                                fullWidth
                                select
                                variant="standard"
                                disabled={hasSectorChildren || !isCDTEC}
                                helperText={getParentSectorHelperText(sectorChildren)}
                              >
                                <MenuItem value={NO_PARENT_SECTOR}>Ninguno</MenuItem>
                                {brotherSectors
                                  .filter((sector) => !sector.parentSector
                                    || sector.parentSector === NO_PARENT_SECTOR)
                                  .map((sector) => (
                                    <MenuItem key={sector.id} value={sector.id}>
                                      {sector.name}
                                    </MenuItem>
                                  ))}
                              </TextField>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Box>
                    <CultivationCyclesList
                      cultivationCycles={
                        cultivationCycles.map((cycle) => ({
                          name: cycle.name,
                          id: cycle.id,
                          startDate: cycle.startDate,
                          endDate: cycle.endDate,
                          varietyName: cycle.variety?.name ?? '',
                          varietySpecies: cycle.variety?.species ?? '',
                          producedKg: cycle.producedKg,
                          exportablePercentage:
                            cycle.exportablePercentage,
                          productionQuality:
                            cycle.productionQuality,
                        }))
                      }
                      sectorId={sectorId}
                      varietiesList={varietiesList ?? []}
                      speciesList={speciesList}
                      setCultivationCyclesFunction={setCultivationCycles}
                      setPhenologicalStatesWCycle={setPhenologicalStatesWCycle}
                      setRefreshPhenologicalStates={setRefreshPhenologicalStates}
                    />
                    <MapWrapper>
                      <SectorPolygonMap
                        sectorId={sectorId}
                        sectorInfo={sectorInfo}
                        setSectorInfo={setSectorInfo}
                      />
                    </MapWrapper>

                    {/* Módulo de Recuperación de Datos */}
                    {isAdminCDTEC && (
                      <DataRecoveryCard />
                    )}

                    {canEdit && (
                      <Grid container justifyContent="flex-end" spacing={3}>
                        <Grid item xs={4} md={2}>
                          <CustomButton
                            disabled={loadingSaveSector}
                            setLoadingSpinnerWhenDisabled={true}
                            buttonText='Guardar cambios'
                            color='blue'
                            onClick={handleSaveChanges}
                          />
                        </Grid>
                        {canDelete && (
                          <Grid item xs={4} md={2}>
                            <CustomButton
                              buttonText='Eliminar Sector'
                              color='red'
                              onClick={() => setDeleteModalOpen(true)}
                            />
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </TabPanel>
                  <TabPanel value="3">
                    <PhenologicalStatesList
                      cultivationCycles={cultivationCycles.map((cycle) => ({
                        id: cycle.id,
                        name: cycle.name,
                        startDate: cycle.startDate,
                        endDate: cycle.endDate,
                        varietyName: cycle.variety?.name ?? '',
                        varietySpecies: cycle.variety?.species ?? '',
                        producedKg: cycle.producedKg,
                        exportablePercentage:
                          cycle.exportablePercentage,
                        productionQuality:
                          cycle.productionQuality,
                      }))}
                      phenologicalStatesWCycle={
                        phenologicalStatesWCycle
                      }
                      setRefreshPhenologicalStates={setRefreshPhenologicalStates}
                    />
                  </TabPanel>
                  <TabPanel value="4">
                    <AlertsTab
                      sectorId={sectorId}
                      headers={ALL_ALERTS_LIST_UI}
                      extraColumns={(alert) => ({
                        sector: sectorInfo?.name,
                        chart: alert.chart,
                      })}
                    />
                  </TabPanel>
                  <TabPanel value="5"></TabPanel>
                  <TabPanel value="7">
                    <Box mt={1}>
                      <Card>
                        <CardContent>
                          <Typography variant="h4" color="primary" gutterBottom>
                            Configuración de Riego
                          </Typography>
                          <Divider className={classes.divider} />
                          {isParentSectorAssigned && (
                            <Typography variant="body1" color="textSecondary" sx={{ mt: 2, mb: 2 }}>
                              Este sector hereda su configuración de riego del sector padre.
                            </Typography>
                          )}
                          <Grid container spacing={2}>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Tipo de riego"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'typeOfIrrigation')}
                                onChange={(e) => handleIrrigationFieldChange('typeOfIrrigation', e.target.value)}
                                fullWidth
                                select
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                              >
                                <MenuItem value="Gotero">Gotero</MenuItem>
                                <MenuItem value="Aspersor">Aspersor</MenuItem>
                                <MenuItem value="Otro">Otro</MenuItem>
                              </TextField>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Precipitación teórica [mm/h]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'theoreticalPrecipitation')}
                                onChange={(e) => handleIrrigationFieldChange('theoreticalPrecipitation', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Caudal emisor [lt/h]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'emitterFlow')}
                                onChange={(e) => handleIrrigationFieldChange('emitterFlow', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Distancia entre plantas [m]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'plantDistance')}
                                onChange={(e) => handleIrrigationFieldChange('plantDistance', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Plantas por hectarea [unidades]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'plantsPerHectare')}
                                onChange={(e) => handleIrrigationFieldChange('plantsPerHectare', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Distancia entre emisores [m]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'transmittersDistance')}
                                onChange={(e) => handleIrrigationFieldChange('transmittersDistance', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Nº de lineas o Asperción [unidades]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'lineNumber')}
                                onChange={(e) => handleIrrigationFieldChange('lineNumber', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Distancia entre hileras [m]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'rowDistance')}
                                onChange={(e) => handleIrrigationFieldChange('rowDistance', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Nº de emisores por hectarea [unidades]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'emittersPerHectare')}
                                onChange={(e) => handleIrrigationFieldChange('emittersPerHectare', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Superficie por emisor [HA]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'emitterSurface')}
                                onChange={(e) => handleIrrigationFieldChange('emitterSurface', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Precipitación real por aforo [mm]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'realPrecipitation')}
                                onChange={(e) => handleIrrigationFieldChange('realPrecipitation', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Largo Hilera desde Caudalímetro [m]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'lengthHileraFromCaudalimeter')}
                                onChange={(e) => handleIrrigationFieldChange('lengthHileraFromCaudalimeter', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                              <TextField
                                label="Volumen Caudalímetro [lt x pulso]"
                                type="text"
                                value={getIrrigationValueFromSectorInfo(sectorInfo, inheritedConfig, 'volumeCaudalimeter')}
                                onChange={(e) => handleIrrigationFieldChange('volumeCaudalimeter', e.target.value)}
                                fullWidth
                                variant="standard"
                                disabled={isParentSectorAssigned || !canEdit}
                                placeholder="0.0"
                              />
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Box>
                    {!isParentSectorAssigned && (
                      <Grid container justifyContent="flex-end" spacing={3} sx={{ mt: 1 }}>
                        <Grid item xs={4} md={2}>
                          <CustomButton
                            disabled={loadingSaveSector}
                            setLoadingSpinnerWhenDisabled={true}
                            buttonText='Guardar configuración'
                            color='blue'
                            onClick={() => handleUpdateIrrigationConfig(
                              sectorInfo,
                              sectorId,
                              selectedFarm,
                              enqueueSnackbar,
                              setLoadingSaveSector,
                              dispatch,
                            )}
                          />
                        </Grid>
                      </Grid>
                    )}
                  </TabPanel>
                  <TabPanel value="8">
                    <RiegoSectorDetail
                      tabPanel={true}
                    />
                  </TabPanel>
                  <TabPanel value="9">
                    <BiweeklyReportTab
                      sectorId={sectorId}
                    />
                  </TabPanel>
                  <TabPanel value="10">
                    <CustomGrapherTab
                      farmId={selectedFarm?.id ?? ''}
                      sectorId={sectorId}
                      wiseconnId={sectorInfo?.wiseconnId}
                      weatherZoneId={sectorInfo?.weatherZoneId}
                      sn={sectorInfo?.sn}
                      telemetryType={sectorInfo?.telemetryType}
                    />
                  </TabPanel>
                </TabContext>
              </Box>
              {(value === '1') && (
                <FilterSidebar
                  selectedVarietyName={selectedVarietyName ?? ''}
                  selectedSpecies={selectedSpecies}
                  sectorInfo={sectorInfo}
                  inheritedConfig={inheritedConfig}
                  value={value}
                  dashboardType={dashboardType}
                  selectedInterval={selectedInterval ?? ''}
                  tempStartDate={tempStartDate}
                  tempEndDate={tempEndDate}
                  selectedCultivationCycle={selectedCultivationCycle}
                  selectedPhenologicalState={selectedPhenologicalState}
                  cultivationCycles={cultivationCycles}
                  phenologicalStatesByCycle={phenologicalStatesWCycle}
                  onDashboardTypeChange={(value2) => {
                    dispatch(setDashboardType(value2));
                    const params = new URLSearchParams(location.search);
                    params.set('type', DASHBOARD_TYPE_TO_PARAM[value2] ?? 'suelo');
                    history.replace({ search: params.toString() });
                  }}
                  onIntervalSelect={handleIntervalSelect}
                  onStartDateChange={(date) => setTempStartDate(date)}
                  onEndDateChange={(date) => setTempEndDate(date)}
                  onCultivationCycleChange={handleCultivationCycleChange}
                  onPhenologicalStateChange={handlePhenologicalStateChange}
                  onApplyDates={handleApplyDates}
                  collapsed={filterCollapsed}
                  setCollapsed={setFilterCollapsed}
                />
              )}
            </Box>
          </DashboardDownloadProvider>
        </ChartRulerProvider>
        </ChartExceptionProvider>
      </Container>
      <CustomConfirmationModal
        open={showUnsavedChangesModal}
        title="Cambios no guardados"
        message="Tienes cambios sin guardar. ¿Deseas guardar los cambios antes de cambiar de pestaña?"
        onConfirm={handleSaveChanges}
        onCancel={() => handleCancelTabChange(
          sectorId,
          setLoading,
          setSectorInfo,
          setIsEditingInfoTab,
          setShowUnsavedChangesModal,
          pendingTabChange,
          setValue,
          setPendingTabChange,
        )}
        confirmButtonColor="confirm"
        confirmText='Guardar cambios'
        cancelText='Descartar cambios'
      />
      <CustomConfirmationModal
        open={deleteModalOpen}
        title="Eliminar Sector"
        message="¿Estás seguro de que quieres eliminar este sector?"
        onConfirm={() => handleDeleteSector(
          sectorId,
          selectedFarm,
          selectedSector,
          enqueueSnackbar,
          setDeleteModalOpen,
          dispatch,
          history,
        )}
        onCancel={() => setDeleteModalOpen(false)}
        confirmButtonColor="delete"
        confirmText='Eliminar'
      />
    </Page>
  );
};

export default SectorDetail;
